import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {IRosterItem, IRosterPlayer} from "data/types/roster";
import type {Empty} from "data/types/global";
import {sumBy, map} from "lodash";
import {type IPlayerCalculations, PlayerCalculations} from "data/utils/player.calculations";
import {RosterPlayStatus} from "data/enums";

interface IParams {
	roster: Empty<IRosterItem>;
	opponentRoster: Empty<IRosterItem>;
	contestId: number;
}

export interface IH2hRosterMatchUpViewController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get rostersList(): Empty<IRosterPlayer>[][];

	get projPts(): string;

	get dailyPts(): string;

	get opponentProjPts(): string;

	get opponentDailyPts(): string;

	get rosterStatus(): RosterPlayStatus;

	get opponentStatus(): RosterPlayStatus;
}

@injectable()
export class H2hRosterMatchUpViewController implements IH2hRosterMatchUpViewController {
	@observable _roster: Empty<IRosterItem>;
	@observable _opponentRoster: Empty<IRosterItem>;
	@observable _contestId: number = 0;
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayerCalculations)
		public readonly _playerCalculations: IPlayerCalculations
	) {
		makeAutoObservable(this);
	}

	get rostersList() {
		const rosterPlayers = this._roster?.players || [];
		const opponentRosterPlayers = this._opponentRoster?.players || [];
		const ownSize = rosterPlayers.length;
		const opponentSize = opponentRosterPlayers.length;
		const biggestRoster = ownSize > opponentSize ? rosterPlayers : opponentRosterPlayers;

		return (biggestRoster || []).map((_it, index) => {
			const ownPlayer = rosterPlayers[index];
			const opponentPlayer = opponentRosterPlayers[index];

			return [ownPlayer, opponentPlayer];
		});
	}

	get projPts() {
		const players = this.rosterGamePlayers;

		const points = sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.projectedPoints || 0;
		});

		return `${points || "--"}`;
	}

	get dailyPts() {
		const players = this.rosterGamePlayers;

		const points = sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.fantasyPoints || 0;
		});

		return `${points || "--"}`;
	}

	get opponentProjPts() {
		const players = this.opponentRosterGamePlayers;

		const points = sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.projectedPoints || 0;
		});

		return `${points || "--"}`;
	}

	get opponentDailyPts() {
		const players = this.opponentRosterGamePlayers;

		const points = sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.fantasyPoints || 0;
		});

		return `${points || "--"}`;
	}

	private get rosterGamePlayers() {
		const players = this._roster?.players;
		if (!players?.length) {
			return [];
		}
		return map(players, (rosterPlayer) => {
			return this._playerCalculations.getGamePlayerById(
				rosterPlayer.playerId,
				this._contestId
			);
		});
	}

	private get opponentRosterGamePlayers() {
		const players = this._opponentRoster?.players;
		if (!players?.length) {
			return [];
		}
		return map(players, (rosterPlayer) => {
			return this._playerCalculations.getGamePlayerById(
				rosterPlayer.playerId,
				this._contestId
			);
		});
	}

	get rosterStatus() {
		return PlayerCalculations.getCommonPlayersStatus(this.rosterGamePlayers);
	}

	get opponentStatus() {
		return PlayerCalculations.getCommonPlayersStatus(this.opponentRosterGamePlayers);
	}

	init(param: IParams) {
		this._opponentRoster = param.opponentRoster;
		this._roster = param.roster;
		this._contestId = param.contestId;
	}

	onChange(param: IParams) {
		this.init(param);
	}
}
